import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Send, Paperclip, Download, MessageCircle, Loader2, ArrowLeft, Users, User, MessageSquare } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import { useChat } from '../../hooks/useChat.jsx'
import api from '../../services/api'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

const ROLE_COLORS = {
  Admin: 'bg-purple-600',
  'Project Manager': 'bg-blue-600',
  Consultant: 'bg-green-600',
  Finance: 'bg-yellow-600',
  Funder: 'bg-orange-600',
}

function Avatar({ name, role, size = 'w-7 h-7 text-[11px]' }) {
  const initials = name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?'
  const color = ROLE_COLORS[role] || 'bg-gray-500'
  return (
    <div className={`${size} rounded-full ${color} flex items-center justify-center text-white font-bold shrink-0`}>
      {initials}
    </div>
  )
}

function Message({ msg, isConsecutive }) {
  const isMine = msg.is_mine

  const downloadFile = async () => {
    try {
      const r = await api.get(`/chat/file/${msg.id}`, { responseType: 'blob' })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = msg.file_name || 'file'
      document.body.appendChild(a); a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { alert('Download failed') }
  }

  return (
    <div className={`flex gap-2 ${isMine ? 'flex-row-reverse' : 'flex-row'} ${isConsecutive ? 'mt-0.5' : 'mt-3'}`}>
      <div className="w-7 shrink-0">
        {!isConsecutive && <Avatar name={msg.sender?.full_name} role={msg.sender?.role} />}
      </div>

      <div className={`flex flex-col max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
        {!isConsecutive && (
          <div className={`flex items-center gap-2 mb-0.5 ${isMine ? 'flex-row-reverse' : ''}`}>
            <span className="text-[11px] font-semibold text-gray-700">{msg.sender?.full_name}</span>
            <span className="text-[10px] text-gray-400">{msg.sender?.role}</span>
            <span className="text-[10px] text-gray-300">
              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
        )}

        <div className={`rounded-2xl px-3 py-2 text-sm wrap-break-word ${
          isMine
            ? 'bg-blue-600 text-white rounded-tr-sm'
            : 'bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm'
        }`}>
          {msg.content && msg.content !== msg.file_name && (
            <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
          )}
          {msg.file_key && (
            <div className={`mt-1 ${msg.content && msg.content !== msg.file_name ? 'pt-2 border-t ' + (isMine ? 'border-blue-400' : 'border-gray-200') : ''}`}>
              <button onClick={downloadFile} className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 ${isMine ? 'bg-blue-500 hover:bg-blue-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                <Download className="w-3 h-3" />
                {msg.file_name || 'Download file'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ChatInput({ onSend, sending, disabled }) {
  const { t: tChat } = useTranslation('assistant')
  const [text, setText] = useState('')
  const [file, setFile] = useState(null)
  const fileRef = useRef()

  const send = () => {
    if ((!text.trim() && !file) || disabled) return
    onSend(text, file)
    setText(''); setFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <div className="border-t border-gray-100 bg-white p-3">
      {file && (
        <div className="mb-2 flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
          <Paperclip className="w-3 h-3 text-blue-600" />
          <span className="text-blue-700 truncate">{file.name}</span>
          <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = '' }} className="ml-auto text-gray-400 hover:text-gray-600">×</button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition shrink-0"
          title={tChat('t_attach_file2')}
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />

        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder={tChat('ph_type_message')}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-28 overflow-y-auto"
          style={{ minHeight: '38px' }}
        />

        <button
          onClick={send}
          disabled={(!text.trim() && !file) || disabled || sending}
          className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 transition shrink-0"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  )
}

// ── Direct tab: WhatsApp-style contact list ───────────────────────────────────

function ContactList({ onPick, open }) {
  const { t: tChat } = useTranslation('assistant')
  const [contacts, setContacts] = useState(null)

  const load = useCallback(() => {
    api.get('/chat/contacts').then(r => setContacts(r.data || [])).catch(() => setContacts([]))
  }, [])

  useEffect(() => {
    if (!open) return
    load()
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [open, load])

  if (contacts === null) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
  }
  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs text-center gap-2 px-6">
        <User className="w-8 h-8 opacity-30" />
        <p>No contacts yet. You can message the people on your projects once you're assigned together.</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-gray-100">
      {contacts.map(c => (
        <button
          key={c.id}
          onClick={() => onPick(c)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50/60 transition text-left"
        >
          <Avatar name={c.full_name} role={c.role} size="w-10 h-10 text-sm" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 truncate">{c.full_name}</p>
              {c.last_at && (
                <span className="text-[10px] text-gray-400 shrink-0">
                  {new Date(c.last_at).toLocaleDateString() === new Date().toLocaleDateString()
                    ? new Date(c.last_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : formatDate(c.last_at)}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-gray-500 truncate">
                {c.last_message
                  ? <>{c.last_is_mine && <span className="text-gray-400">{tChat('you_label')} </span>}{c.last_message}</>
                  : <span className="italic text-gray-400">{c.role} — start the conversation</span>}
              </p>
              {c.unread > 0 && (
                <span className="bg-green-500 text-white text-[10px] font-bold min-w-4.5 h-4.5 px-1 rounded-full flex items-center justify-center shrink-0">
                  {c.unread}
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

// ── Direct tab: the 1:1 thread ────────────────────────────────────────────────

function DMThread({ contact, open, onBack }) {
  const bottomRef = useRef()
  const { messages, loading, sending, sendMessage } = useChat('dm', contact.id, open)

  useEffect(() => {
    api.put(`/chat/dm/${contact.id}/read`).catch(() => {})
  }, [contact.id, messages.length])

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60)
  }, [messages.length])

  return (
    <>
      <div className="flex items-center gap-2.5 px-3 py-2 bg-blue-50 border-b border-blue-100 shrink-0">
        <button onClick={onBack} className="p-1 text-gray-500 hover:text-gray-800 hover:bg-white rounded-lg">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <Avatar name={contact.full_name} role={contact.role} size="w-8 h-8 text-xs" />
        <div>
          <p className="text-sm font-semibold text-gray-900 leading-tight">{contact.full_name}</p>
          <p className="text-[10px] text-gray-500">{contact.role} · private conversation</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs gap-2">
            <MessageCircle className="w-8 h-8 opacity-30" />
            <p>Say hello to {contact.full_name.split(' ')[0]} — only the two of you can see this chat.</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const prev = messages[i - 1]
            const isConsecutive = prev && prev.sender?.id === msg.sender?.id &&
              (new Date(msg.created_at) - new Date(prev.created_at)) < 120000
            return <Message key={msg.id} msg={msg} isConsecutive={isConsecutive} />
          })
        )}
        <div ref={bottomRef} />
      </div>

      <ChatInput onSend={sendMessage} sending={sending} />
    </>
  )
}

// ── Milestone threads for the selected project — surfaces the per-milestone chats that
// previously were only reachable by opening the project page (header panel only had DM +
// project-group tabs, so a milestone-chat bell notification had nowhere to "land" visibly).
function MilestoneThreadsList({ projectId, onClose }) {
  const { t: tChat } = useTranslation('assistant')
  const navigate = useNavigate()
  const [threads, setThreads] = useState(null)

  useEffect(() => {
    if (!projectId) { setThreads(null); return }
    let cancelled = false
    const load = () => api.get(`/chat/project/${projectId}/milestone-threads`)
      .then(r => { if (!cancelled) setThreads(r.data || []) })
      .catch(() => { if (!cancelled) setThreads([]) })
    load()
    const t = setInterval(load, 15000)
    return () => { cancelled = true; clearInterval(t) }
  }, [projectId])

  if (!threads || threads.length === 0) return null

  const openThread = (milestoneId) => {
    onClose?.()
    navigate(`/projects/${projectId}?milestone=${milestoneId}`)
  }

  return (
    <div className="border-t border-blue-100 bg-white">
      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{tChat('milestone_chats')}</p>
      <div className="max-h-32 overflow-y-auto divide-y divide-gray-50">
        {threads.map(t => (
          <button
            key={t.milestone_id}
            onClick={() => openThread(t.milestone_id)}
            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50/60 transition text-left"
          >
            <MessageSquare className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-800 truncate">{t.milestone_title}</p>
              <p className="text-[11px] text-gray-500 truncate">
                {t.last_sender ? <span className="text-gray-400">{t.last_sender}: </span> : ''}{t.last_message}
              </p>
            </div>
            {t.unread > 0 && (
              <span className="bg-green-500 text-white text-[10px] font-bold min-w-4.5 h-4.5 px-1 rounded-full flex items-center justify-center shrink-0">
                {t.unread}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Projects tab: the original group chat ─────────────────────────────────────

function ProjectGroupChat({ open, onClose }) {
  const { t: tChat } = useTranslation('assistant')
  const { user } = useAuth()
  const { projects } = useProjects()
  const [selectedProject, setSelectedProject] = useState(null)
  const [members, setMembers] = useState([])
  const bottomRef = useRef()

  // Memoized so the array identity is stable across re-renders (only recomputes when the
  // underlying projects list or user actually changes) — an unstable identity here was
  // contributing to the chat occasionally blanking on selection.
  const myProjects = useMemo(() => (projects || []).filter(p => {
    if (!p || ['Archived'].includes(p.status)) return false
    if (user?.role === 'Admin') return true
    if (user?.role === 'Project Manager') return p.project_manager_id === user.id || p.projectManagerId === user.id
    if (user?.role === 'Consultant') return p.consultant_id === user.id || p.consultantId === user.id
    return false
  }), [projects, user?.role, user?.id])

  useEffect(() => {
    if (myProjects.length > 0 && !selectedProject) setSelectedProject(myProjects[0])
  }, [myProjects.length])

  useEffect(() => {
    if (!selectedProject?.id) return
    api.get(`/chat/project/${selectedProject.id}/members`)
      .then(r => setMembers(r.data || []))
      .catch(() => setMembers([]))
  }, [selectedProject?.id])

  const { messages, loading, sending, sendMessage, markRead } = useChat('project', selectedProject?.id, open)

  useEffect(() => {
    if (open) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
  }, [messages.length, open])

  useEffect(() => {
    if (open && selectedProject?.id) markRead(selectedProject.id)
  }, [open, selectedProject?.id])

  if (myProjects.length === 0) {
    return <div className="flex-1 flex items-center justify-center text-gray-400 text-sm bg-gray-50">{tChat('no_active_projects_chat')}</div>
  }

  return (
    <>
      <div className="bg-blue-50 border-b border-blue-100 px-3 py-2 shrink-0 space-y-1.5">
        {myProjects.length > 1 && (
          <select
            value={selectedProject?.id || ''}
            onChange={(e) => setSelectedProject(myProjects.find(p => p.id === parseInt(e.target.value)))}
            className="w-full text-xs border border-blue-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {myProjects.map(p => <option key={p.id} value={p.id}>{p.name?.slice(0, 50)}</option>)}
          </select>
        )}
        {members.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-gray-400 text-[10px]">{tChat('group_label')}</span>
            {members.map(m => (
              <span key={m.id} className="flex items-center gap-1 bg-white rounded-full px-2 py-0.5 border border-blue-100">
                <span className="text-[10px] font-medium text-gray-700">{m.full_name?.split(' ')[0]}</span>
                <span className="text-gray-400 text-[9px]">({m.role?.replace('Project Manager', 'PM')})</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-2 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs text-center gap-2">
            <Users className="w-8 h-8 opacity-30" />
            <p>{tChat('no_messages_group')}</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const prev = messages[i - 1]
            const isConsecutive = prev && prev.sender?.id === msg.sender?.id &&
              (new Date(msg.created_at) - new Date(prev.created_at)) < 120000
            return <Message key={msg.id} msg={msg} isConsecutive={isConsecutive} />
          })
        )}
        <div ref={bottomRef} />
      </div>

      <MilestoneThreadsList projectId={selectedProject?.id} onClose={onClose} />

      <ChatInput onSend={sendMessage} sending={sending} disabled={!selectedProject} />
    </>
  )
}

// ── Main floating chat hub ────────────────────────────────────────────────────

const PANEL_W = 384
const PANEL_H = 540
const CHAT_POS_KEY = 'chat3p-pos'

export default function ProjectChat({ open, onClose }) {
  const { t: tChat } = useTranslation('assistant')
  const { user } = useAuth()
  const [tab, setTab] = useState('direct')          // 'direct' | 'projects'
  const [activeContact, setActiveContact] = useState(null)

  // Finance & Funder have direct messages only — project group chat is
  // execution-focused (Admin · PM · Consultant)
  const hasProjectGroups = ['Admin', 'Project Manager', 'Consultant'].includes(user?.role)

  // ── Draggable by the header bar, position persisted ─────────────────────────
  const clamp = (p) => ({
    x: Math.min(Math.max(p.x, 4), window.innerWidth - PANEL_W - 4),
    y: Math.min(Math.max(p.y, 4), window.innerHeight - 120),
  })
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CHAT_POS_KEY))
      if (saved && typeof saved.x === 'number') return clamp(saved)
    } catch { /* ignore */ }
    return null  // null = default docked bottom-right
  })
  const dragRef = useRef(null)

  const onHeaderPointerDown = (e) => {
    if (e.target.closest('button')) return  // tabs/close still clickable
    const rect = e.currentTarget.parentElement.getBoundingClientRect()
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    const onMove = (ev) => {
      const d = dragRef.current
      if (!d) return
      setPos(clamp({ x: ev.clientX - d.dx, y: ev.clientY - d.dy }))
    }
    const onUp = () => {
      dragRef.current = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      setPos(p => { if (p) localStorage.setItem(CHAT_POS_KEY, JSON.stringify(p)); return p })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  useEffect(() => {
    const onResize = () => setPos(p => (p ? clamp(p) : p))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  if (!open) return null

  const panelPosition = pos
    ? { left: pos.x, top: pos.y, height: PANEL_H, maxHeight: 'calc(100vh - 72px)' }
    : { bottom: 0, right: 16, height: PANEL_H, maxHeight: 'calc(100vh - 72px)' }

  return (
    <div className={`fixed z-50 w-80 sm:w-96 flex flex-col shadow-2xl overflow-hidden border border-gray-200 bg-white ${pos ? 'rounded-2xl' : 'rounded-t-2xl'}`}
         style={panelPosition}>

      {/* Header — drag handle */}
      <div
        onPointerDown={onHeaderPointerDown}
        className="bg-linear-to-r from-blue-600 to-indigo-600 px-4 pt-3 pb-0 shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
        title={tChat('t_drag_to_move')}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-white" />
            <span className="text-white font-semibold text-sm">{tChat('chats')}</span>
            <span className="text-blue-200 text-[10px]">⠿ drag to move</span>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {/* Tabs */}
        <div className="flex gap-1">
          {[
            { id: 'direct', label: 'Direct', icon: User },
            ...(hasProjectGroups ? [{ id: 'projects', label: 'Project Groups', icon: Users }] : []),
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setActiveContact(null) }}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition ${
                tab === t.id ? 'bg-white text-blue-700' : 'text-blue-100 hover:bg-white/10'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      {tab === 'direct' ? (
        activeContact ? (
          <DMThread contact={activeContact} open={open} onBack={() => setActiveContact(null)} />
        ) : (
          <div className="flex-1 overflow-y-auto">
            <ContactList onPick={setActiveContact} open={open} />
          </div>
        )
      ) : (
        <ProjectGroupChat open={open} onClose={onClose} />
      )}
    </div>
  )
}
